import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  deleteOfflineCurAccountRecord,
  getOfflineCurAccounts,
  uploadOfflineCurAccountFile,
} from "../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import UploadOfflineCurModal from "./components/UploadOfflineCurModal";
import { ACCOUNT_DETAIL_FONT_SX } from "../../components/AccountDetailPolicy/accountDetailTypography";
import { EditAccountTablePagination } from "../../components/AccountDetailPolicy/editAccountTableControls";

const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.25,
  px: 1.5,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.25,
  px: 1.5,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};

const SEARCH_FIELD_SX = {
  flex: { xs: "1 1 160px", sm: "1 1 220px" },
  minWidth: { xs: 0, sm: 220 },
  maxWidth: { xs: "100%", sm: 360 },
  "& .MuiOutlinedInput-root": {
    height: 40,
    fontSize: "0.875rem",
    backgroundColor: "white",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    py: 0,
    height: "100%",
    boxSizing: "border-box",
  },
};

const FILTER_SELECT_SX = {
  flex: { xs: "1 1 160px", sm: "0 0 auto" },
  width: { xs: "100%", sm: 200 },
  minWidth: { xs: 0, sm: 180 },
  "& .MuiOutlinedInput-root": {
    height: 40,
    fontSize: "0.875rem",
    backgroundColor: "white",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": {
      borderColor: "#111827",
      borderWidth: "1px",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
    color: "#64748b",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#111827",
  },
};

const FILTER_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
      "& .MuiMenuItem-root": {
        fontSize: "0.875rem",
      },
      "& .MuiMenuItem-root.Mui-selected": {
        backgroundColor: "rgba(249, 129, 37, 0.12)",
      },
      "& .MuiMenuItem-root.Mui-selected:hover": {
        backgroundColor: "rgba(249, 129, 37, 0.18)",
      },
      "& .MuiMenuItem-root:hover": {
        backgroundColor: "#fff4eb",
      },
    },
  },
};

const PRIMARY_BTN_SX = {
  height: 40,
  minHeight: 40,
  flex: { xs: "1 1 auto", sm: "0 0 auto" },
  backgroundColor: "#F98125",
  color: "#fff",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2,
  py: 0,
  boxShadow: "none",
  whiteSpace: "nowrap",
  lineHeight: 1,
  "& .MuiButton-startIcon": { mr: 0.75 },
  "&:hover": { backgroundColor: "#e8741c", boxShadow: "none" },
  "&.Mui-disabled": {
    backgroundColor: "#e5e7eb",
    color: "#9ca3af",
  },
};

const DELETE_BTN_SX = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  border: "1px solid #fecaca",
  backgroundColor: "#fef2f2",
  color: "#dc2626",
  padding: 0,
  "&:hover": {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
  },
  "&.Mui-disabled": {
    color: "#fca5a5",
    borderColor: "#fee2e2",
    backgroundColor: "#fff",
  },
};

/** API sends UTC without a `Z` suffix; parse as UTC so toLocaleString can show browser-local time. */
const parseUtcTimestamp = (value) => {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  const hasExplicitTz =
    /[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s) || /[+-]\d{4}$/.test(s);
  const d = new Date(hasExplicitTz ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatTimestamp = (isoString) => {
  const d = parseUtcTimestamp(isoString);
  if (!d) return "—";
  try {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch (error) {
    return String(isoString);
  }
};

/** Same pattern as Dashboard `WAFRInsight`: paper surface, border, shadow, arrow ring. */
const offlineCurDashboardTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: "background.paper",
      color: "text.primary",
      border: "1px solid",
      borderColor: "divider",
      boxShadow: 3,
      maxWidth: "min(90vw, 22rem)",
      p: 0,
      "& .MuiTooltip-arrow": {
        color: "background.paper",
        "&::before": {
          border: "1px solid",
          borderColor: "divider",
        },
      },
    },
  },
  /** Popper flips / shifts by default; do not force placement — MUI default is `bottom`. */
  popper: {
    modifiers: [
      { name: "flip", enabled: true },
      { name: "preventOverflow", enabled: true, options: { padding: 8 } },
    ],
  },
};

const formatPeriodLabel = (periodRaw) => {
  const text = String(periodRaw ?? "").trim();
  const m = text.match(/^(\d{4})-(\d{2})$/);
  if (!m) return text || "—";
  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return text;
  const d = new Date(Date.UTC(year, monthIndex, 1));
  try {
    return d.toLocaleString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
  } catch (error) {
    return text;
  }
};

const normalizeOfflineCurRow = (raw, index) => {
  const accountNumber =
    raw.accountNumber != null && String(raw.accountNumber).trim() !== ""
      ? String(raw.accountNumber).trim()
      : raw.account_number != null && String(raw.account_number).trim() !== ""
        ? String(raw.account_number).trim()
        : raw.account_id != null
          ? String(raw.account_id)
          : raw.accountId != null
            ? String(raw.accountId)
            : "—";

  const periodRaw =
    raw.period != null && String(raw.period).trim() !== ""
      ? String(raw.period).trim()
      : raw.year != null && raw.month != null
        ? `${raw.year}-${String(raw.month).padStart(2, "0")}`
        : "—";

  const uploadedAt =
    raw.uploadedDate ??
    raw.uploaded_date ??
    raw.uploaded_at ??
    raw.uploadedAt ??
    raw.created_at ??
    raw.createdAt ??
    null;

  const id =
    raw.id ??
    raw.uuid ??
    `${accountNumber}-${uploadedAt ?? index}-${raw.fileName ?? index}`;

  return {
    id: String(id),
    accountNumber,
    username:
      raw.username != null && String(raw.username).trim() !== ""
        ? String(raw.username).trim()
        : raw.user_name ?? raw.userName ?? "—",
    period: periodRaw,
    fileName:
      raw.fileName ??
      raw.file_name ??
      raw.filename ??
      raw.original_filename ??
      raw.originalFilename ??
      "—",
    processType:
      raw.processType ??
      raw.process_type ??
      raw.type ??
      "—",
    status:
      raw.status ??
      raw.upload_status ??
      raw.uploadStatus ??
      raw.state ??
      "—",
    uploadedAt,
    processedAt:
      raw.processedDate ??
      raw.processed_date ??
      raw.processed_at ??
      raw.processedAt ??
      null,
    errorText:
      raw.errorText != null && String(raw.errorText).trim() !== ""
        ? String(raw.errorText).trim()
        : raw.error_text != null && String(raw.error_text).trim() !== ""
          ? String(raw.error_text).trim()
          : raw.errorMessage != null && String(raw.errorMessage).trim() !== ""
            ? String(raw.errorMessage).trim()
            : null,
  };
};

/** Normalize API `accountNumbers` for dropdowns (sorted, unique). */
const normalizeAccountNumbersList = (raw) => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const list = [];
  raw.forEach((x) => {
    const s = x != null ? String(x).trim() : "";
    if (s && !seen.has(s)) {
      seen.add(s);
      list.push(s);
    }
  });
  return list.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
};

const extractOfflineCurItems = (resp) => {
  if (!resp || typeof resp !== "object") return [];
  if (resp.status != null && resp.status >= 400) return [];

  const fromObject = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    if (Array.isArray(obj)) return obj;
    for (const key of [
      "auditList",
      "items",
      "accounts",
      "results",
      "content",
      "records",
      "rows",
    ]) {
      if (Array.isArray(obj[key])) return obj[key];
    }
    if (Array.isArray(obj.data)) return obj.data;
    if (obj.data && typeof obj.data === "object" && Array.isArray(obj.data.items)) {
      return obj.data.items;
    }
    return null;
  };

  const direct = fromObject(resp);
  if (direct) return direct;

  const numericKeys = Object.keys(resp).filter((k) => k !== "status" && k !== "isSynthetic" && /^\d+$/.test(k));
  if (numericKeys.length > 0) {
    return numericKeys
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => resp[k])
      .filter((row) => row != null && typeof row === "object");
  }

  return [];
};

const formatStatusDisplay = (statusRaw) => {
  const normalized = String(statusRaw ?? "").trim().toUpperCase();
  if (normalized === "UPLOADED") {
    return { label: "Queued", severity: "default", reason: "" };
  }
  if (normalized === "REJECTED") {
    return {
      label: "Failed",
      severity: "error",
      reason: "Rejected due to incorrect file",
    };
  }
  return {
    label: String(statusRaw ?? "—").trim() || "—",
    severity: "default",
    reason: "",
  };
};

/** Processed time only; does not change status labels or chips. */
const shouldHideOfflineCurProcessedAt = (statusRaw) => {
  const w = String(statusRaw ?? "").trim().toUpperCase();
  if (w === "UPLOADED") return true;
  if (w === "REJECTED" || w === "FAILED" || w === "VALIDATION_FAILED") return true;
  if (w.includes("FAILED") && !w.startsWith("NOT_")) return true;
  return false;
};

const getOfflineCurDeleteDisabledReason = (statusRaw) => {
  const normalized = String(statusRaw ?? "").trim().toUpperCase();
  if (normalized === "PROCESSED") {
    return "Cannot delete: this upload has already been processed";
  }
  if (normalized === "PROCESSING") {
    return "Cannot delete: this upload is currently processing";
  }
  if (normalized === "DISPATCHED") {
    return "Cannot delete: this upload has been dispatched";
  }
  return null;
};

const OfflineCur = () => {
  const [accountFilter, setAccountFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const cancelUploadRef = useRef(false);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [rows, setRows] = useState([]);
  /** From API `accountNumbers` — used for filter dropdown and upload modal. */
  const [accountNumbersList, setAccountNumbersList] = useState([]);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [deleteConfirmRow, setDeleteConfirmRow] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const loadAccounts = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    setRows([]);
    setAccountNumbersList([]);
    try {
      const response = await getOfflineCurAccounts();
      if (response?.status >= 200 && response?.status < 300) {
        const items = extractOfflineCurItems(response);
        setRows(items.map((item, i) => normalizeOfflineCurRow(item, i)));
        setAccountNumbersList(normalizeAccountNumbersList(response.accountNumbers));
        return;
      }
      setRows([]);
      setAccountNumbersList([]);
      setListError(getApiErrorMessage(response));
    } catch (error) {
      setRows([]);
      setAccountNumbersList([]);
      setListError(getApiErrorMessage(error?.response?.data ?? error?.data ?? error));
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
    return () => {
      setRows([]);
      setAccountNumbersList([]);
    };
  }, [loadAccounts]);

  useEffect(() => {
    const next = 'Upload CUR2.0';
    const previous = document.title;
    document.title = next;
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    if (accountFilter && !accountNumbersList.includes(accountFilter)) {
      setAccountFilter("");
    }
  }, [accountNumbersList, accountFilter]);

  const periodOptions = useMemo(() => {
    const uniquePeriods = new Set();
    rows.forEach((row) => {
      const p = String(row.period ?? "").trim();
      if (p && p !== "—") uniquePeriods.add(p);
    });
    return Array.from(uniquePeriods).sort((a, b) => b.localeCompare(a));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let next = rows;
    if (accountFilter) {
      next = next.filter((row) => String(row.accountNumber).trim() === accountFilter);
    }
    if (periodFilter) {
      next = next.filter((row) => String(row.period).trim() === periodFilter);
    }
    if (!searchTerm.trim()) return next;
    const normalizedSearch = searchTerm.toLowerCase();
    return next.filter((row) => {
      const statusText = formatStatusDisplay(row.status).label.toLowerCase();
      return (
        String(row.accountNumber).toLowerCase().includes(normalizedSearch) ||
        String(row.username).toLowerCase().includes(normalizedSearch) ||
        String(row.period).toLowerCase().includes(normalizedSearch) ||
        String(row.fileName).toLowerCase().includes(normalizedSearch) ||
        String(row.processType).toLowerCase().includes(normalizedSearch) ||
        String(row.errorText ?? "").toLowerCase().includes(normalizedSearch) ||
        statusText.includes(normalizedSearch)
      );
    });
  }, [rows, accountFilter, periodFilter, searchTerm]);

  useEffect(() => {
    if (periodFilter && !periodOptions.includes(periodFilter)) {
      setPeriodFilter("");
    }
  }, [periodFilter, periodOptions]);

  const groupedRows = useMemo(() => {
    const groupedByAccount = new Map();
    filteredRows.forEach((row) => {
      const accountKey = String(row.accountNumber || "—");
      if (!groupedByAccount.has(accountKey)) {
        groupedByAccount.set(accountKey, new Map());
      }
      const periodKey = String(row.period || "—");
      const periodMap = groupedByAccount.get(accountKey);
      if (!periodMap.has(periodKey)) {
        periodMap.set(periodKey, []);
      }
      periodMap.get(periodKey).push(row);
    });

    return Array.from(groupedByAccount.entries())
      .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
      .map(([accountNumber, periodMap]) => {
        const periods = Array.from(periodMap.entries())
          .sort((a, b) => b[0].localeCompare(a[0]))
          .map(([period, items]) => ({
            period,
            key: `${accountNumber}__${period}`,
            items: [...items].sort((x, y) => {
              const xT = parseUtcTimestamp(x.uploadedAt)?.getTime() ?? 0;
              const yT = parseUtcTimestamp(y.uploadedAt)?.getTime() ?? 0;
              return yT - xT;
            }),
          }));
        return {
          accountNumber,
          key: `acct__${accountNumber}`,
          periods,
          totalItems: periods.reduce((sum, p) => sum + p.items.length, 0),
        };
      });
  }, [filteredRows]);

  const totalGroups = groupedRows.length;
  const maxPage = Math.max(0, Math.ceil(totalGroups / rowsPerPage) - 1);
  const pagedGroups = useMemo(() => {
    const start = page * rowsPerPage;
    return groupedRows.slice(start, start + rowsPerPage);
  }, [groupedRows, page, rowsPerPage]);

  useEffect(() => {
    if (page > maxPage) setPage(maxPage);
  }, [page, maxPage]);

  useEffect(() => {
    setPage(0);
  }, [accountFilter, periodFilter, searchTerm, rowsPerPage]);

  useEffect(() => {
    setExpandedAccounts((prev) => {
      const next = { ...prev };
      pagedGroups.forEach((group) => {
        if (next[group.key] == null) next[group.key] = true;
      });
      return next;
    });
  }, [pagedGroups]);

  useEffect(() => {
    setExpandedPeriods((prev) => {
      const next = { ...prev };
      pagedGroups.forEach((group) => {
        group.periods.forEach((periodGroup) => {
          if (next[periodGroup.key] == null) next[periodGroup.key] = true;
        });
      });
      return next;
    });
  }, [pagedGroups]);

  const handleUpload = async ({ accountNumber, year, month, file, files }) => {
    const fileList =
      Array.isArray(files) && files.length > 0 ? files : file ? [file] : [];
    if (fileList.length === 0) return false;

    cancelUploadRef.current = false;
    setIsUploading(true);
    setUploadProgress(null);

    try {
      for (let i = 0; i < fileList.length; i += 1) {
        if (cancelUploadRef.current) {
          setSnackbarState({
            open: true,
            message: "Upload cancelled.",
            severity: "info",
          });
          return false;
        }

        setUploadProgress({
          fileIndex: i + 1,
          fileCount: fileList.length,
          currentFileName: fileList[i].name,
        });

        const response = await uploadOfflineCurAccountFile({
          accountNumber,
          year,
          month,
          file: fileList[i],
          onProgress: (p) =>
            setUploadProgress({
              ...p,
              fileIndex: i + 1,
              fileCount: fileList.length,
              currentFileName: fileList[i].name,
            }),
          shouldAbort: () => cancelUploadRef.current,
        });

        if (response?.status === 499) {
          setSnackbarState({
            open: true,
            message: response?.data?.message || "Upload cancelled.",
            severity: "info",
          });
          return false;
        }

        if (!(response?.status >= 200 && response?.status < 300)) {
          setSnackbarState({
            open: true,
            message:
              fileList.length > 1
                ? `${getApiErrorMessage(response?.data ?? response)} (file ${i + 1} of ${
                    fileList.length
                  }: ${fileList[i].name})`
                : getApiErrorMessage(response?.data ?? response),
            severity: "error",
          });
          return false;
        }
      }

      await loadAccounts();
      setSnackbarState({
        open: true,
        message:
          fileList.length > 1
            ? `${fileList.length} file(s) uploaded successfully.`
            : "CUR2.0 file uploaded successfully.",
        severity: "success",
      });
      return true;
    } catch (error) {
      setSnackbarState({
        open: true,
        message: getApiErrorMessage(error?.response?.data ?? error?.data ?? error),
        severity: "error",
      });
      return false;
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  const requestCancelUpload = useCallback(() => {
    cancelUploadRef.current = true;
  }, []);

  const toggleAccountGroup = (accountKey) => {
    setExpandedAccounts((prev) => ({ ...prev, [accountKey]: !prev[accountKey] }));
  };

  const togglePeriodGroup = (periodKey) => {
    setExpandedPeriods((prev) => ({ ...prev, [periodKey]: !prev[periodKey] }));
  };

  const handleConfirmDeleteOfflineCur = async () => {
    if (!deleteConfirmRow) return;
    if (getOfflineCurDeleteDisabledReason(deleteConfirmRow.status)) return;
    const accountNumber = String(deleteConfirmRow.accountNumber ?? "").trim();
    const fileName = deleteConfirmRow.fileName;
    const period = String(deleteConfirmRow.period ?? "").trim();
    const fileNameTrimmed = fileName != null ? String(fileName).trim() : "";
    if (
      !accountNumber ||
      accountNumber === "—" ||
      !period ||
      period === "—" ||
      !fileNameTrimmed ||
      fileNameTrimmed === "—"
    ) {
      setSnackbarState({
        open: true,
        message: "Missing account, period, or file name for this record.",
        severity: "error",
      });
      return;
    }
    setDeleteSubmitting(true);
    try {
      const response = await deleteOfflineCurAccountRecord({
        accountNumber,
        fileName,
        period,
      });
      if (response?.status >= 200 && response?.status < 300) {
        setDeleteConfirmRow(null);
        await loadAccounts();
        setSnackbarState({
          open: true,
          message: "CUR2.0 upload record removed.",
          severity: "success",
        });
      } else {
        setSnackbarState({
          open: true,
          message: getApiErrorMessage(response?.data ?? response),
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbarState({
        open: true,
        message: getApiErrorMessage(error?.response?.data ?? error?.data ?? error),
        severity: "error",
      });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2.5 },
        backgroundColor: "#f3f4f6",
        boxSizing: "border-box",
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      <Box
        sx={{
          pb: 1.25,
          mb: 1.75,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: "#111827",
            fontSize: "1rem",
          }}
        >
          Upload CUR2.0
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            color: "#64748b",
            fontSize: "0.875rem",
            fontWeight: 400,
            lineHeight: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          Upload and track CUR2.0 (offline account CUR) files.
        </Typography>
      </Box>

      {listError ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setListError(null)}>
          {listError}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", lg: "row" },
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            flex: 1,
            minWidth: 0,
            width: { xs: "100%", lg: "auto" },
          }}
        >
          <TextField
            size="small"
            placeholder="Search period, file, user, status…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={SEARCH_FIELD_SX}
          />
          <FormControl size="small" sx={FILTER_SELECT_SX}>
            <InputLabel id="offline-cur-account-filter-label">Account number</InputLabel>
            <Select
              labelId="offline-cur-account-filter-label"
              label="Account number"
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
              MenuProps={FILTER_MENU_PROPS}
            >
              <MenuItem value="">
                <em>All accounts</em>
              </MenuItem>
              {accountNumbersList.map((acc) => (
                <MenuItem key={acc} value={acc}>
                  {acc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ ...FILTER_SELECT_SX, width: { xs: "100%", sm: 180 } }}>
            <InputLabel id="offline-cur-period-filter-label">Month/year</InputLabel>
            <Select
              labelId="offline-cur-period-filter-label"
              label="Month/year"
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
              MenuProps={FILTER_MENU_PROPS}
            >
              <MenuItem value="">
                <em>All periods</em>
              </MenuItem>
              {periodOptions.map((period) => (
                <MenuItem key={period} value={period}>
                  {formatPeriodLabel(period)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          startIcon={<UploadFileIcon fontSize="small" />}
          onClick={() => setIsUploadModalOpen(true)}
          sx={PRIMARY_BTN_SX}
        >
          Upload
        </Button>
      </Box>

      <Alert
        severity="info"
        sx={{
          mb: 2,
          fontSize: "0.8125rem",
          borderRadius: "8px",
          backgroundColor: "#eff6ff",
          color: "#1e40af",
          border: "1px solid #bfdbfe",
          "& .MuiAlert-icon": { color: "#2563eb" },
          "& .MuiAlert-message": { width: "100%" },
        }}
      >
        CUR file processing happens everyday at 09:00 EST.
      </Alert>

      <Box
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          backgroundColor: "#fff",
          overflow: "hidden",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <TableContainer
          sx={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            maxHeight: { xs: "none", md: "calc(100vh - 420px)" },
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              width: "100%",
              minWidth: { xs: 720, md: 960 },
              tableLayout: "fixed",
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...HEADER_CELL_SX, width: "4%" }} />
                <TableCell sx={{ ...HEADER_CELL_SX, width: "14%" }}>Account / Period</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: "22%" }}>File name</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: "10%" }}>Type</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: "10%" }}>Status</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: "12%" }}>Processed</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: "18%" }}>Uploaded</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: "8%" }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
              <TableBody>
                {listLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ ...BODY_CELL_SX, py: 6 }}>
                      <CircularProgress size={28} sx={{ color: "#64748b" }} />
                    </TableCell>
                  </TableRow>
                ) : groupedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ ...BODY_CELL_SX, py: 6, color: "#94a3b8" }}>
                      No CUR2.0 uploads found.
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedGroups.map((accountGroup) => {
                    const accountOpen = expandedAccounts[accountGroup.key] !== false;
                    return (
                      <React.Fragment key={accountGroup.key}>
                        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                          <TableCell sx={{ ...BODY_CELL_SX, py: 1, px: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => toggleAccountGroup(accountGroup.key)}
                              sx={{ color: "#64748b" }}
                            >
                              {accountOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell colSpan={7} sx={{ ...BODY_CELL_SX, py: 1.25, fontWeight: 600, color: "#111827" }}>
                            {accountGroup.accountNumber}
                            <Typography component="span" sx={{ ml: 1, color: "#64748b", fontSize: "0.75rem", fontWeight: 500 }}>
                              ({accountGroup.totalItems} file{accountGroup.totalItems === 1 ? "" : "s"})
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {accountOpen &&
                          accountGroup.periods.map((periodGroup) => {
                            const periodOpen = expandedPeriods[periodGroup.key] !== false;
                            return (
                              <React.Fragment key={periodGroup.key}>
                                <TableRow sx={{ backgroundColor: "#fafbfc" }}>
                                  <TableCell sx={{ ...BODY_CELL_SX, py: 1, px: 1, pl: 3 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => togglePeriodGroup(periodGroup.key)}
                                      sx={{ color: "#64748b" }}
                                    >
                                      {periodOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell colSpan={7} sx={{ ...BODY_CELL_SX, py: 1, fontWeight: 600, color: "#334155" }}>
                                    {formatPeriodLabel(periodGroup.period)}
                                    <Typography component="span" sx={{ ml: 1, color: "#64748b", fontSize: "0.75rem", fontWeight: 500 }}>
                                      ({periodGroup.items.length} upload{periodGroup.items.length === 1 ? "" : "s"})
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                                {periodOpen &&
                                  periodGroup.items.map((row) => {
                                    const statusMeta = formatStatusDisplay(row.status);
                                    const hideProcessedAt = shouldHideOfflineCurProcessedAt(row.status);
                                    const deleteDisabledReason = getOfflineCurDeleteDisabledReason(row.status);
                                    const deleteDisabled = Boolean(deleteDisabledReason);
                                    const hasApiErrorText =
                                      row.errorText != null && String(row.errorText).trim() !== "";
                                    const showStatusHelp = hasApiErrorText || Boolean(statusMeta.reason);
                                    const statusChipOnly = (
                                      <Chip
                                        label={statusMeta.label}
                                        size="small"
                                        color={statusMeta.severity === "error" ? "error" : "default"}
                                        variant={statusMeta.severity === "error" ? "filled" : "outlined"}
                                      />
                                    );
                                    const statusWithHint = (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          maxWidth: "100%",
                                        }}
                                      >
                                        {statusChipOnly}
                                        <ErrorOutlineIcon
                                          sx={{
                                            fontSize: 18,
                                            color: "error.main",
                                            flexShrink: 0,
                                          }}
                                          aria-hidden
                                        />
                                      </Box>
                                    );
                                    return (
                                      <TableRow
                                        key={row.id}
                                        sx={{
                                          "&:hover": { backgroundColor: "#f8fafc" },
                                        }}
                                      >
                                        <TableCell sx={{ ...BODY_CELL_SX, pl: 5 }} />
                                        <TableCell sx={{ ...BODY_CELL_SX, color: "#64748b" }}>
                                          {formatPeriodLabel(row.period)}
                                        </TableCell>
                                        <TableCell sx={{ ...BODY_CELL_SX, wordBreak: "break-all" }}>
                                          {row.fileName}
                                        </TableCell>
                                        <TableCell sx={BODY_CELL_SX}>{row.processType}</TableCell>
                                        <TableCell sx={BODY_CELL_SX}>
                                          {showStatusHelp ? (
                                            <Tooltip
                                              arrow
                                              interactive={hasApiErrorText}
                                              slotProps={
                                                hasApiErrorText
                                                  ? {
                                                      ...offlineCurDashboardTooltipSlotProps,
                                                      tooltip: {
                                                        ...offlineCurDashboardTooltipSlotProps.tooltip,
                                                        sx: {
                                                          ...offlineCurDashboardTooltipSlotProps.tooltip.sx,
                                                          maxWidth: "min(95vw, 52rem)",
                                                        },
                                                      },
                                                    }
                                                  : offlineCurDashboardTooltipSlotProps
                                              }
                                              title={
                                                hasApiErrorText ? (
                                                  <Box sx={{ p: 1, minWidth: 200, maxWidth: "min(95vw, 52rem)" }}>
                                                    <Typography
                                                      variant="body2"
                                                      sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
                                                    >
                                                      Error details
                                                    </Typography>
                                                    <Typography
                                                      variant="body2"
                                                      sx={{
                                                        color: "text.secondary",
                                                        whiteSpace: "pre-wrap",
                                                        wordBreak: "break-word",
                                                        lineHeight: 1.5,
                                                      }}
                                                    >
                                                      {String(row.errorText ?? "").trim()}
                                                    </Typography>
                                                  </Box>
                                                ) : (
                                                  <Box sx={{ p: 1, maxWidth: 320 }}>
                                                    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
                                                      {statusMeta.reason}
                                                    </Typography>
                                                  </Box>
                                                )
                                              }
                                            >
                                              {statusWithHint}
                                            </Tooltip>
                                          ) : (
                                            statusChipOnly
                                          )}
                                        </TableCell>
                                        <TableCell sx={BODY_CELL_SX}>
                                          {hideProcessedAt ? "—" : formatTimestamp(row.processedAt)}
                                        </TableCell>
                                        <TableCell sx={BODY_CELL_SX}>
                                          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, wordBreak: "break-word" }}>
                                            {row.username}
                                          </Typography>
                                          <Typography sx={{ fontSize: "0.75rem", color: "#64748b", mt: 0.25 }}>
                                            {formatTimestamp(row.uploadedAt)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ ...BODY_CELL_SX, pr: 1.5 }}>
                                          <Tooltip title={deleteDisabledReason ?? "Delete upload"}>
                                            <span>
                                              <IconButton
                                                size="small"
                                                aria-label="Delete CUR2.0 upload record"
                                                disabled={listLoading || deleteDisabled}
                                                onClick={() => setDeleteConfirmRow(row)}
                                                sx={DELETE_BTN_SX}
                                              >
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                              </React.Fragment>
                            );
                          })}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!listLoading && groupedRows.length > 0 ? (
            <EditAccountTablePagination
              page={page}
              rowsPerPage={rowsPerPage}
              total={totalGroups}
              onPageChange={setPage}
              onRowsPerPageChange={(value) => {
                setRowsPerPage(value);
                setPage(0);
              }}
              rowsPerPageOptions={[3, 5, 10, 20]}
            />
          ) : null}
        </Box>

      <UploadOfflineCurModal
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSubmit={handleUpload}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        onRequestCancelUpload={requestCancelUpload}
        accountNumbers={accountNumbersList}
      />

      <Dialog
        open={Boolean(deleteConfirmRow)}
        onClose={() => {
          if (!deleteSubmitting) setDeleteConfirmRow(null);
        }}
        aria-labelledby="offline-cur-delete-dialog-title"
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            width: { xs: "calc(100% - 24px)", sm: "100%" },
            m: { xs: 1.5, sm: 2 },
            overflow: "hidden",
            ...ACCOUNT_DETAIL_FONT_SX,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: "#F98125",
          }}
        >
          <Typography
            id="offline-cur-delete-dialog-title"
            sx={{
              fontWeight: 700,
              fontSize: "1.15rem",
              color: "#FFFFFF",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Confirm Delete
          </Typography>
          <button
            type="button"
            aria-label="Close"
            onClick={() => {
              if (!deleteSubmitting) setDeleteConfirmRow(null);
            }}
            disabled={deleteSubmitting}
            style={{
              padding: 0,
              margin: 0,
              border: "none",
              background: "transparent",
              color: "#FFFFFF",
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: deleteSubmitting ? "not-allowed" : "pointer",
              opacity: deleteSubmitting ? 0.6 : 1,
              boxShadow: "none",
              flexShrink: 0,
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Typography
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 400,
              color: "#334155",
              lineHeight: 1.5,
              wordBreak: "break-word",
              mb: 1.5,
            }}
          >
            Remove this CUR2.0 upload record for account{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "#111827" }}>
              {deleteConfirmRow?.accountNumber}
            </Box>
            , period{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "#111827" }}>
              {deleteConfirmRow ? formatPeriodLabel(deleteConfirmRow.period) : ""}
            </Box>
            ?
          </Typography>
          {deleteConfirmRow?.fileName && String(deleteConfirmRow.fileName).trim() !== "—" ? (
            <Typography
              sx={{
                color: "#64748b",
                fontSize: "0.8125rem",
                mb: 2,
                wordBreak: "break-all",
              }}
            >
              File:{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "#334155" }}>
                {String(deleteConfirmRow.fileName)}
              </Box>
            </Typography>
          ) : null}
          <Box
            sx={{
              backgroundColor: "#fef2f2",
              p: 1.5,
              borderRadius: "8px",
              border: "1px solid #fecaca",
            }}
          >
            <Typography sx={{ color: "#991b1b", fontWeight: 500, fontSize: "0.8125rem" }}>
              This action cannot be undone. This upload record will be permanently deleted.
            </Typography>
          </Box>
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.25,
              width: "100%",
            }}
          >
            <Button
              onClick={() => setDeleteConfirmRow(null)}
              disabled={deleteSubmitting}
              variant="contained"
              disableElevation
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                borderRadius: "8px",
                px: 2.5,
                py: 0.85,
                color: "#fff",
                backgroundColor: "#6c757d",
                border: "1px solid #6c757d",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#5a6268",
                  borderColor: "#5a6268",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#adb5bd",
                  borderColor: "#adb5bd",
                  color: "#fff",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteOfflineCur}
              disabled={deleteSubmitting}
              variant="contained"
              disableElevation
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                borderRadius: "8px",
                px: 2.5,
                backgroundColor: "#ef4444",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#dc2626",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#fca5a5",
                  color: "#fff",
                },
              }}
            >
              {deleteSubmitting ? (
                <>
                  <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
                  Deleting…
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={() => setSnackbarState((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbarState.severity}
          onClose={() => setSnackbarState((prev) => ({ ...prev, open: false }))}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OfflineCur;
